import { createSignal, Show } from 'solid-js';
import { submitLead } from '~/lib/submit';
import { maskPhone, phoneIsValid } from '~/lib/phone';
import { FormConsent, FormSubmitDisclaimer } from '~/components/FormConsent';
import './LeadForm.css';

type Status = 'idle' | 'sending' | 'success' | 'error';

interface Errors {
  name?: string;
  phone?: string;
  vehicle?: string;
  part?: string;
}

interface Props {
  source?: string;
}

export function LeadForm(props: Props) {
  const [name, setName] = createSignal('');
  const [phone, setPhone] = createSignal('');
  const [vehicle, setVehicle] = createSignal('');
  const [part, setPart] = createSignal('');
  const [vin, setVin] = createSignal('');
  const [comment, setComment] = createSignal('');
  const [photo, setPhoto] = createSignal<File | null>(null);
  const [errors, setErrors] = createSignal<Errors>({});
  const [status, setStatus] = createSignal<Status>('idle');
  const [errorMessage, setErrorMessage] = createSignal('');
  const [agreed, setAgreed] = createSignal(false);
  const [agreedError, setAgreedError] = createSignal(false);

  const onPhoneInput = (e: InputEvent) => {
    const target = e.target as HTMLInputElement;
    setPhone(maskPhone(target.value));
  };

  const validate = (): boolean => {
    const e: Errors = {};
    // Обязателен только телефон — на него я перезвоню.
    // Имя, марка техники и название агрегата теперь опциональны.
    if (!phoneIsValid(phone())) e.phone = 'Введите номер полностью';
    setErrors(e);
    const consentOk = agreed();
    setAgreedError(!consentOk);
    return Object.keys(e).length === 0 && consentOk;
  };

  const resetForm = () => {
    setName('');
    setPhone('');
    setVehicle('');
    setPart('');
    setVin('');
    setComment('');
    setPhoto(null);
    setErrors({});
    setAgreed(false);
    setAgreedError(false);
  };

  const onSubmit = async (e: SubmitEvent) => {
    e.preventDefault();
    if (status() === 'sending') return;
    if (!validate()) return;

    setStatus('sending');
    const res = await submitLead({
      name: name().trim(),
      phone: phone(),
      vehicle: vehicle().trim(),
      part: part().trim(),
      vin: vin().trim(),
      comment: comment().trim(),
      photoName: photo()?.name,
      source: props.source ?? 'landing',
    });

    if (res.ok) {
      setStatus('success');
      resetForm();
    } else {
      setStatus('error');
      setErrorMessage(res.message ?? 'Не удалось отправить заявку');
    }
  };

  const reopen = () => {
    setStatus('idle');
    setErrorMessage('');
  };

  return (
    <section class="section leadform" id="lead">
      <div class="container">
        <div class="leadform__wrap" data-reveal>
          <div class="leadform__intro">
            <div class="eyebrow">Заявка</div>
            <h2 class="leadform__title">
              Подберём двигатель или&nbsp;запчасть для&nbsp;вашей техники
            </h2>
            <p class="leadform__lead">
              Оставьте номер — я&nbsp;свяжусь с&nbsp;вами и&nbsp;уточню детали:
              наличие, совместимость, варианты по&nbsp;цене и&nbsp;срокам.
            </p>
            <ul class="leadform__bullets">
              <li>Свяжемся в течение рабочего дня</li>
              <li>Не передаём данные третьим лицам</li>
              <li>Работаем с НДС и без НДС</li>
            </ul>
          </div>

          <div class="leadform__form-wrap">
            <Show
              when={status() !== 'success'}
              fallback={
                <div class="leadform__state leadform__state--success" role="status">
                  <div class="leadform__state-icon" aria-hidden="true">✓</div>
                  <h3>Заявка отправлена</h3>
                  <p>Я свяжусь с вами в течение рабочего дня. Если срочно — звоните +7 (343) 385-85-13.</p>
                  <button type="button" class="btn btn--ghost" onClick={reopen}>
                    Отправить ещё одну
                  </button>
                </div>
              }
            >
              <form class="leadform__form" onSubmit={onSubmit} noValidate>
                <div class="leadform__row">
                  <label class="leadform__field">
                    <span>Имя <em class="leadform__opt">(необязательно)</em></span>
                    <input
                      type="text"
                      autocomplete="name"
                      placeholder="Как к вам обращаться"
                      value={name()}
                      onInput={(e) => setName(e.currentTarget.value)}
                    />
                  </label>

                  <label class="leadform__field" classList={{ 'has-error': !!errors().phone }}>
                    <span>
                      Телефон <em class="leadform__req">обязательное</em>
                    </span>
                    <input
                      type="tel"
                      autocomplete="tel"
                      placeholder="+7 (___) ___-__-__"
                      value={phone()}
                      onInput={onPhoneInput}
                      inputmode="tel"
                      required
                      aria-required="true"
                    />
                    <Show
                      when={!errors().phone}
                      fallback={<span class="leadform__error">{errors().phone}</span>}
                    >
                      <span class="leadform__hint">Перезвоню в рабочее время.</span>
                    </Show>
                  </label>
                </div>

                <div class="leadform__row">
                  <label class="leadform__field">
                    <span>Марка и модель техники <em class="leadform__opt">(необязательно)</em></span>
                    <input
                      type="text"
                      placeholder="Например, КАМАЗ 65115 или Caterpillar 980H"
                      value={vehicle()}
                      onInput={(e) => setVehicle(e.currentTarget.value)}
                    />
                  </label>

                  <label class="leadform__field">
                    <span>Какой агрегат требуется <em class="leadform__opt">(необязательно)</em></span>
                    <input
                      type="text"
                      placeholder="Двигатель, КПП, мост, форсунки…"
                      value={part()}
                      onInput={(e) => setPart(e.currentTarget.value)}
                    />
                  </label>
                </div>

                <div class="leadform__row">
                  <label class="leadform__field">
                    <span>VIN или номер двигателя</span>
                    <input
                      type="text"
                      placeholder="VIN, серийный или каталожный номер"
                      value={vin()}
                      onInput={(e) => setVin(e.currentTarget.value)}
                    />
                  </label>

                  <label class="leadform__field leadform__file">
                    <span>Фото шильдика</span>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => setPhoto(e.currentTarget.files?.[0] ?? null)}
                    />
                    <span class="leadform__file-btn">
                      {photo()?.name ?? 'Загрузить файл'}
                    </span>
                  </label>
                </div>

                <label class="leadform__field">
                  <span>Комментарий</span>
                  <textarea
                    rows="3"
                    placeholder="Уточнения по модификации, состоянию или срокам"
                    value={comment()}
                    onInput={(e) => setComment(e.currentTarget.value)}
                  />
                </label>

                <Show when={status() === 'error'}>
                  <div class="leadform__state leadform__state--error" role="alert">
                    Не удалось отправить заявку. {errorMessage()} Попробуйте ещё раз или позвоните нам.
                  </div>
                </Show>

                <FormConsent
                  checked={agreed()}
                  onChange={(v) => {
                    setAgreed(v);
                    if (v) setAgreedError(false);
                  }}
                  error={agreedError()}
                />

                <div class="leadform__submit">
                  <button
                    type="submit"
                    class="btn btn--red"
                    disabled={status() === 'sending'}
                  >
                    {status() === 'sending' ? 'Отправляем…' : 'Отправить на подбор'}
                    <Show when={status() !== 'sending'}>
                      <span class="btn__arrow">→</span>
                    </Show>
                  </button>
                  <FormSubmitDisclaimer />
                </div>
              </form>
            </Show>
          </div>
        </div>
      </div>
    </section>
  );
}
